import { observer } from "mobx-react";
import info from "../../../../info.svg";
import useStore from "../../../../../hooks/useStore";
import numToRoman from "../../../../../helpers/numToRoman";
import CustomTooltip from "../../../../../utils/tooltip/tooltip";
import { calculateAqiData } from "../../../../../helpers/groupDataCalculations";
import { useEffect, useMemo } from "react";
import { getUnits } from "../../../../../utils/helpers";

const accumulateResult = (data, key) =>
  data.reduce((acc, curr) => {
    const val = Number(curr[key]);
    return acc + (isNaN(val) || val === null || val === undefined ? 0 : val);
  }, 0);

const Row = ({ data, index, frequency, setData, group, totalData }) => {
  const {
    companyStore: { units, kpiTarget },
  } = useStore();

  const onChangeHandler = (event) => {
    const { name, value, files } = event.target;
    setData((prev) =>
      calculateAqiData(
        prev.map((ele) => {
          if (ele.group === group) {
            const temp = ele.value.map((e) => {
              if (e.fieldId === data.fieldId) {
                return {
                  ...e,
                  ...frequency,
                  [name]:
                    name === "attachment"
                      ? [...(e.attachment || []), ...Array.from(files || [])] // Properly convert FileList to an array
                      : value,
                };
              }
              return e;
            });
            return {
              ...ele,
              value: temp,
              target: accumulateResult(temp, "target"),
              prevValue: accumulateResult(temp, "prevValue"),
            };
          }
          return ele;
        })
      )
    );
  };

  const cancelHandler = (file) => {
    setData((prev) =>
      prev.map((ele) => {
        if (ele.group === group) {
          const temp = ele.value.map((e) => {
            if (e.fieldId === data.fieldId) {
              const updatedAttachments = e.attachment.filter(
                (item) => item.name !== file.name
              );
              return {
                ...e,
                ...frequency,
                attachment: updatedAttachments,
              };
            }
            return e;
          });
          return {
            ...ele,
            value: temp,
          };
        }
        return ele;
      })
    );
  };


  const calculateScope1And2Intensity = (totalData, type = "prevValue") => {
    const groupD = totalData.find((g) => g.group === "D")?.value || [];
    const groupA = totalData.find((g) => g.group === "A")?.value || [];
  
    const scope1Val = groupD.find((e) => e.fieldName === "Scope-1")?.[type];
    const scope2Val = groupD.find((e) => e.fieldName === "Scope-2")?.[type];
    const finishedGoodsVal = groupA.find((e) => e.fieldName === "Finished goods quantity produced")?.[type];
    
    const scope1 = Number(scope1Val);
    const scope2 = Number(scope2Val);
    const finishedGoods = Number(finishedGoodsVal);
    
    // Check for invalid numbers
    if (isNaN(scope1) || isNaN(scope2) || isNaN(finishedGoods)) {
      return "0";
    }
  
    const totalEmission = scope1 + scope2;
  
    if (!finishedGoods || finishedGoods <= 0) return "0";
  
    const result = totalEmission / finishedGoods;
    
    // Check if result is valid
    if (isNaN(result) || !isFinite(result)) {
      return "0";
    }
  
    return result.toFixed(2);
  };
  

  const isDisabled = useMemo(
    () =>
      [
        "Scope-1 Emission Intensity/Revenue",
        "Scope-2 Emission Intensity/Revenue",
        "Scope-3 Emission Intensity/Revenue",
        "Scope-3",
        "Scope-1",
        "Scope-2",
        "Scope-1 Emission Intensity/Finished goods quantity produced",
        "Scope-2 Emission Intensity/Finished goods quantity produced",
        "Scope-3 Emission Intensity/Finished goods quantity produced",
        "Scope 1 & 2 Emission Intensity",
     
      ].includes(data.fieldName),
    [data.fieldName]
  );

  const revenue =
    units
      ?.find(
        (ele) =>
          ele.id ===
          totalData
            ?.find((e) => e.group === "A")
            ?.value?.find((e) => e.fieldName === "Revenue")?.unit
      )
      ?.name?.split(" ") || [];

  const revenueUnit =
    revenue?.length === 1 ? "rupees" : revenue[revenue.length - 1];

  const unitField = units.find(
    (ele) =>
      ele.id === totalData?.find((ele) => ele.group === "A")?.value?.[0]?.unit
  )?.shortName;

  const finishedGoods = units?.find(
    (ele) =>
      ele.id ===
      totalData
        ?.find((e) => e.group === "A")
        ?.value?.find((e) => e.fieldName === "Finished goods quantity produced")
        ?.unit
  )?.shortName;

const baseUnits = getUnits({ units, data, type: "aqi" });


const dataUnits = [
  "Scope-1 Emission Intensity/Revenue",
  "Scope-2 Emission Intensity/Revenue",
  "Scope-3 Emission Intensity/Revenue",
  "GHG Emission Intensity/Revenue",
].includes(data.fieldName)
  ? baseUnits.filter(
      (ele) =>
        ele.name.includes(
          ["dollar", "rupees", "euro"].includes(revenueUnit)
            ? revenueUnit
            : ""
        ) && ele.name.includes(unitField || "")
    )
  : [
      "Scope-1 Emission Intensity/Finished goods quantity produced",
      "Scope-2 Emission Intensity/Finished goods quantity produced",
      "Scope-3 Emission Intensity/Finished goods quantity produced",
      "GHG Emission Intensity/Finished goods quantity produced",
      "Scope 1 & 2 Emission Intensity",
    ].includes(data.fieldName)
  ? baseUnits.filter(
      (ele) =>
        ele.shortName === "t-CO2/MT" ||
        (ele.shortName.includes(
          ["MT", "Kg"].includes(finishedGoods) ? `/${finishedGoods}` : ""
        ) && ele.shortName.includes(unitField || ""))
    )
  : baseUnits;


  // Get the unit from the store directly
const tco2Unit = units.find((u) => u.shortName === "t-CO2/MT");
const finalDataUnits =
  data.fieldName === "Scope 1 & 2 Emission Intensity" && tco2Unit
    ? [...dataUnits, ...(!dataUnits.some((u) => u.id === tco2Unit.id) ? [tco2Unit] : [])]
    : dataUnits;




    useEffect(() => {
      if (data.fieldName === "Scope 1 & 2 Emission Intensity") {
        const prev = calculateScope1And2Intensity(totalData, "prevValue");
        const target = calculateScope1And2Intensity(totalData, "target");
    
        setData((prevData) =>
          calculateAqiData(
            prevData.map((ele) => {
              if (ele.group === group) {
                const updated = ele.value.map((e) =>
                  e.fieldId === data.fieldId
                    ? { ...e, prevValue: prev, target: target }
                    : e
                );
                return {
                  ...ele,
                  value: updated,
                  target: accumulateResult(updated, "target"),
                  prevValue: accumulateResult(updated, "prevValue"),
                };
              }
              return ele;
            })
          )
        );
      }
    }, [totalData, data.fieldId, data.fieldName, group]);

    useEffect(() => {
    if (!data) return;

    const match = kpiTarget?.find(
      (kt) => kt.fieldId === data?.fieldId && kt.group === data?.group
    );

    let dividedTarget = 0;

    if (match?.target) {
      dividedTarget = match.target;
      if (frequency?.frequencyType === "Monthly") {
        dividedTarget = match.target / 12;
      } else if (frequency?.frequencyType === "Daily") {
        const year = Number(
          frequency?.frequencyYear ||
            new Date(frequency?.frequencyDate).getFullYear()
        );
        const isLeapYear =
          (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
        dividedTarget = match.target / (isLeapYear ? 366 : 365);
      }
    }

    setData((prev) =>
      calculateAqiData(
        prev.map((ele) => {
          if (ele.group === group) {
            const updated = ele.value.map((e) => {
              if (e.fieldId === data.fieldId) {
                return {
                  ...e,
                  target: dividedTarget.toFixed(2), // Set even if 0
                };
              }
              return e;
            });

            return {
              ...ele,
              value: updated,
              target: accumulateResult(updated, "target") || 0,
              prevValue: accumulateResult(updated, "prevValue"),
            };
          }
          return ele;
        })
      )
    );
  }, [
    kpiTarget,
    data?.fieldId,
    data?.group,
    setData,
    group,
    frequency?.frequencyType,
    frequency?.frequencyYear,
    frequency?.frequencyDate,
  ]);

  return (
    <tr key={`${data?.fieldId}-data-row`}>
      <td>{index || index === 0 ? numToRoman(index + 1) : "*"}</td>
      <td>
        <span
          style={{
            display: "flex",
            gap: "5px",
          }}
        >
          {data?.fieldName}
          {data?.guidance && (
            <CustomTooltip position="top" content={data?.guidance}>
              <img src={info} alt="" />
            </CustomTooltip>
          )}
        </span>
      </td>
      <td>
        <select
          className="form-select"
          name="unit"
          //   required={validate && !data.unit}
          required
          onChange={onChangeHandler}
          value={data?.unitId || data.unit}
          style={{ width: "135px" }}
        >
          <option value="">Select Unit</option>
          {finalDataUnits?.map((ele) => (
            <option key={ele.id} value={ele.id}>
              {ele?.shortName}
            </option>
          ))}
        </select>
      </td>
      <td>
        <div>{frequency?.frequencyType || ""}</div>
        <div>{frequency?.frequencyYear || ""}</div>
        <div>{frequency?.frequencyDate || frequency?.frequencyMonth || ""}</div>
      </td>
      <td>
        {/* <input
          disabled={isDisabled}
          type={isDisabled ? "text" : "number"}
          name="prevValue"
          onChange={onChangeHandler}
          value={data.prevValue}
          className="rounded form-control w-100 numberCountInput"
        /> */}
         <input
    disabled={
      isDisabled || data.fieldName === "Scope 1 & 2 Emission Intensity"
    }
    type={
      data.fieldName === "Scope 1 & 2 Emission Intensity" ? "text" : "number"
    }
    name="prevValue"
    onChange={onChangeHandler}
    value={(() => {
      if (data.fieldName === "Scope 1 & 2 Emission Intensity") {
        const calculated = calculateScope1And2Intensity(totalData, "prevValue");
        return calculated === "NaN" || calculated === "Infinity" || calculated === "-Infinity" ? "0" : calculated;
      }
      const val = data.prevValue;
      if (val === null || val === undefined || val === "" || isNaN(val)) return "";
      return val;
    })()}
    className="rounded form-control w-100 numberCountInput"
  />
      </td>
      <td>
        {/* <input
          disabled={isDisabled}
          type={isDisabled ? "text" : "number"}
          name="target"
          onChange={onChangeHandler}
          value={data.target}
          className="rounded form-control w-100 numberCountInput"
        /> */}

<input
    disabled={
      isDisabled || data.fieldName === "Scope 1 & 2 Emission Intensity"
    }
    type={
      data.fieldName === "Scope 1 & 2 Emission Intensity" ? "text" : "number"
    }
    name="target"
    onChange={onChangeHandler}
    value={(() => {
      if (data.fieldName === "Scope 1 & 2 Emission Intensity") {
        const calculated = calculateScope1And2Intensity(totalData, "target");
        return calculated === "NaN" || calculated === "Infinity" || calculated === "-Infinity" ? "0" : calculated;
      }
      const val = data.target;
      if (val === null || val === undefined || val === "" || isNaN(val)) return "";
      return val;
    })()}
    className="rounded form-control w-100 numberCountInput"
  />
      </td>
      <td>
        <textarea
          name="comment"
          value={data.comment}
          onChange={onChangeHandler}
          className="rounded form-control w-100 addFromTxtArea"
        />
      </td>
      {/* <td>
        <input
          name="value"
          value={data.value}
          onChange={onChangeHandler}
          className="rounded form-control w-100"
        />
      </td> */}
      <td>
        {data.attachment && data.attachment.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            }}
          >
            {data.attachment.map((file, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
                className="fileUploader"
              >
                <span>{file?.name.slice(0, 10) + "..."}</span>
                <span
                  onClick={() => cancelHandler(file)}
                  style={{
                    fontWeight: "700",
                    cursor: "pointer",
                    fontSize: "16px",
                    lineHeight: "20px",
                  }}
                  className="crossBtn"
                >
                  X
                </span>
              </div>
            ))}
          </div>
        )}
        <span
          w-100
          style={{
            position: "relative",
          }}
        >
          <div
            className="chooseFile"
            style={{
              position: "absolute",
              border: "1px solid #ccc",
              borderRadius: "4px",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Choose File
          </div>
          <input
            name="attachment"
            onChange={onChangeHandler}
            type="file"
            multiple
            style={{
              opacity: 0,
              cursor: "pointer",
              zIndex: 1,
              top: 0,
              width: "100%",
              height: "100%",
            }}
            className="form-control w-100 h-100"
          />
        </span>
      </td>
    </tr>
  );
};

export default observer(Row);
